import { getCoreCourseContent } from "./courses";
import { normalizer } from "../tipddy-v2";

export const CERTIFICATE_NAME_PREFIXES = ["cert", "diploma"];

/**
 * Mirrors the pre-change ProgressRowItem predicate exactly, including its
 * "last match wins" behavior (the original forEach overwrote its state on
 * every match instead of stopping at the first one).
 * Supports both "cert" and "diploma" prefixes for Linkcap compatibility.
 *
 * @param {{ modules?: Array<{ modules?: Array<{ id: number|string, name: string, uservisible: boolean }> }> }} normalized
 * @returns {string|null}
 */
export function selectCertificateCmid(normalized) {
  let cmid = null;
  const sections = normalized && normalized.modules;
  if (!Array.isArray(sections)) return null;

  for (const section of sections) {
    if (!section || !Array.isArray(section.modules)) continue;
    for (const sub of section.modules) {
      if (
        sub &&
        sub.id != null &&
        typeof sub.name === "string" &&
        CERTIFICATE_NAME_PREFIXES.some((prefix) => sub.name.toLowerCase().startsWith(prefix)) &&
        sub.uservisible
      ) {
        cmid = String(sub.id); // last match wins — matches current forEach overwrite
      }
    }
  }

  return cmid;
}

/**
 * Pure fetch+normalize. No context, no state, no side effects.
 * Throws on network failure or when `normalizer` rejects malformed input.
 *
 * @param {{ token: string, courseId: number|string }} args
 * @returns {Promise<{ modules: Array, online: Array, programs: Array, diagnostic: any }>}
 */
export async function fetchNormalizedCourseContent({ token, courseId }) {
  const data = await getCoreCourseContent({ token, course_id: courseId });
  return normalizer(data);
}

/**
 * Composed, failure-tolerant entry point: fetches, normalizes, and selects
 * the certificate cmid for a single course. Never rejects — resolves to
 * `null` on any failure (network error, malformed content, no match).
 *
 * @param {{ token: string, courseId: number|string }} args
 * @returns {Promise<string|null>}
 */
export async function resolveCourseCertificateCmid({ token, courseId }) {
  try {
    const normalized = await fetchNormalizedCourseContent({ token, courseId });
    return selectCertificateCmid(normalized);
  } catch {
    return null;
  }
}
